import { useState, useCallback } from "react";
import { suppliesService } from "../services/suppliesService";

const LOCAL_STORAGE_PREFIX = "supply_conf_backup_";

export function useSupplyConferences() {
  const [selectedSector, setSelectedSector] = useState("");
  const [activeConferenceId, setActiveConferenceId] = useState(null);
  const [counts, setCounts] = useState({}); // { [supplyId]: number }
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // 1. Seleciona o Setor e carrega rascunhos (do PocketBase ou localStorage)
  const selectSector = useCallback(async (sector, suppliesList) => {
    setSelectedSector(sector);
    setActiveConferenceId(null);
    setCounts({});
    setError(null);

    if (!sector) return;

    setIsSaving(true);
    try {
      // Tenta carregar do PocketBase primeiro
      const pendingConf = await suppliesService.getPendingConference(sector);
      
      // Tenta carregar do localStorage como backup/fallback
      const localBackupRaw = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${sector.toLowerCase()}`);
      let localBackup = null;
      if (localBackupRaw) {
        try {
          localBackup = JSON.parse(localBackupRaw);
        } catch (e) {
          console.error("Erro ao ler backup local:", e);
        }
      }

      if (pendingConf) {
        setActiveConferenceId(pendingConf.id);
        
        // Reconstrói o mapa de contagens a partir dos itens do banco
        const initialCounts = {};
        const dbItems = Array.isArray(pendingConf.items) ? pendingConf.items : [];
        dbItems.forEach((item) => {
          if (item.supply_id) {
            initialCounts[item.supply_id] = item.counted_quantity;
          }
        });

        // Se o backup local for mais recente ou contiver dados que não estão no banco (resolução de conflito offline simples),
        // podemos mesclar ou preferir o mais recente. Para simplicidade e consistência, se houver rascunho no banco,
        // usamos o do banco, mas se houver local com mais itens, avisamos ou mesclamos.
        // Aqui, priorizamos o do banco e limpamos o local se ele bater.
        setCounts(initialCounts);
      } else if (localBackup && localBackup.counts) {
        // Se não há no banco mas tem local (ex: erro de rede anterior ao tentar criar no banco)
        setCounts(localBackup.counts);
      } else {
        // Inicializa com vazio
        setCounts({});
      }
    } catch (err) {
      console.error("Erro ao iniciar conferência de setor:", err);
      setError(err);
    } finally {
      setIsSaving(false);
    }
  }, []);

  // 2. Atualizar contagem de um item específico
  const updateCount = useCallback((supplyId, val) => {
    setCounts((prev) => {
      const current = prev[supplyId] !== undefined ? prev[supplyId] : 0;
      let nextVal = typeof val === "function" ? val(current) : val;
      if (nextVal < 0) nextVal = 0; // não permite estoque negativo físico contado
      
      // Salva backup silencioso no localStorage toda vez que altera
      setTimeout(() => {
        setSelectedSector((sec) => {
          if (sec) {
            const backupData = {
              updatedAt: new Date().toISOString(),
              counts: { ...prev, [supplyId]: nextVal }
            };
            localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${sec.toLowerCase()}`, JSON.stringify(backupData));
          }
          return sec;
        });
      }, 0);

      return {
        ...prev,
        [supplyId]: nextVal,
      };
    });
  }, []);

  // Auxiliar para preparar os itens da conferência
  const prepareConferenceItems = (suppliesList) => {
    // Filtra insumos pertencentes ao setor ativo
    const sectorSupplies = suppliesList.filter(
      (s) => s.sector?.toLowerCase() === selectedSector.toLowerCase()
    );

    return sectorSupplies.map((s) => {
      const counted = counts[s.id] !== undefined ? counts[s.id] : 0;
      const expected = s.current_quantity || 0;
      return {
        supply_id: s.id,
        name: s.name,
        unit: s.unit || "un",
        min_quantity: s.min_quantity || 0,
        expected_quantity: expected,
        counted_quantity: counted,
        discrepancy: counted - expected,
      };
    });
  };

  // 3. Salvar Rascunho (pending)
  const saveDraft = async (suppliesList) => {
    if (!selectedSector) return null;
    setIsSaving(true);
    setError(null);
    try {
      const items = prepareConferenceItems(suppliesList);
      const payload = {
        status: "pending",
        sector_filter: selectedSector.toLowerCase(),
        items: items,
      };

      const record = await suppliesService.saveConference(activeConferenceId, payload);
      setActiveConferenceId(record.id);

      // Atualiza backup local
      const backupData = {
        updatedAt: new Date().toISOString(),
        counts: counts,
        dbId: record.id
      };
      localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${selectedSector.toLowerCase()}`, JSON.stringify(backupData));

      return record;
    } catch (err) {
      console.error("Erro ao salvar rascunho:", err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // 4. Finalizar Conferência (reviewed) e reconciliar estoque
  const finalizeConference = async (suppliesList, onProgress) => {
    if (!selectedSector) return null;
    setIsSaving(true);
    setError(null);
    try {
      const items = prepareConferenceItems(suppliesList);
      const payload = {
        status: "reviewed",
        sector_filter: selectedSector.toLowerCase(),
        items: items,
      };

      // 1. Salva a conferência finalizada no banco
      const record = await suppliesService.saveConference(activeConferenceId, payload);

      // 2. Reconciliação Física: Atualiza a current_quantity de todos os insumos do setor no banco
      const totalItems = items.length;
      for (let i = 0; i < totalItems; i++) {
        const item = items[i];
        if (onProgress) {
          onProgress(item.name, i + 1, totalItems);
        }
        // Chamada atômica de overwrite para sincronizar saldo
        await suppliesService.setSupplyQuantity(item.supply_id, item.counted_quantity);
      }

      // 3. Limpa estados locais e backups do localStorage
      localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}${selectedSector.toLowerCase()}`);
      setCounts({});
      setActiveConferenceId(null);
      setSelectedSector("");

      return record;
    } catch (err) {
      console.error("Erro ao finalizar conferência:", err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    selectedSector,
    activeConferenceId,
    counts,
    isSaving,
    error,
    selectSector,
    updateCount,
    saveDraft,
    finalizeConference,
  };
}
